import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftInput from "components/SoftInput";
import SoftTypography from "components/SoftTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import MarkEmailReadOutlinedIcon from "@mui/icons-material/MarkEmailReadOutlined";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import EventAvailableOutlinedIcon from "@mui/icons-material/EventAvailableOutlined";
import {
  useGetAppSettingsQuery,
  useStartAnnualEmailVerificationMutation,
} from "store/api/settings.api";
import Formatmessage from "services/Formatmessage";
import useLogout from "services/Handlelogout";
import schema from "validationSchema/settings";
import { FadeLoader } from "react-spinners";

const labelSx = {
  fontSize: "12px",
  color: "#3D484E",
  fontWeight: 500,
};

const inputFieldSx = {
  width: "100% !important",
  py: "14px",
  backgroundColor: "#F9F9F9 !important",
  border: "1px solid #BCC8CE !important",
  borderRadius: "12px !important",
  color: "#1A1C1C",
  fontSize: "14px",
  "& .MuiInputBase-input": {
    color: "#1A1C1C !important",
    fontSize: "14px !important",
  },
  "& .MuiInputBase-input::placeholder": {
    color: "#666666 !important",
    fontSize: "14px !important",
    opacity: 1,
  },
};

const formatDate = (value) => {
  if (value === null || value === undefined || value === "") return "—";

  const timestamp = Number(value);
  if (Number.isNaN(timestamp)) return value;

  // API returns Unix timestamps in seconds
  const date = new Date(timestamp * 1000);
  if (Number.isNaN(date.getTime())) return value;

  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

function Settings() {
  const logout = useLogout();
  const [loading, setLoading] = useState(false);
  const [days, setDays] = useState("");
  const [errors, setErrors] = useState({});
  const [verificationSettings, setVerificationSettings] = useState({
    grace_days: null,
    start_date: null,
    needed_before: null,
  });

  const { data, isLoading } = useGetAppSettingsQuery();
  const [startAnnualEmailVerification] = useStartAnnualEmailVerificationMutation();

  useEffect(() => {
    if (data?.status === 1) {
      const settings = data?.data?.annual_email_verification || {};
      setVerificationSettings({
        grace_days: settings?.grace_days ?? null,
        start_date: settings?.start_date ?? null,
        needed_before: settings?.needed_before ?? null,
      });
    } else if (data?.status == 0) {
      toast.error(Formatmessage(data?.message));
    } else if (data?.status == 10) {
      logout();
    }
  }, [data]);

  const handleChange = (e) => {
    const value = e.target.value;
    if (value === "") {
      setDays("");
      setErrors((prev) => ({ ...prev, days: "" }));
      return;
    }

    if (/^\d+$/.test(value) && Number(value) <= 365) {
      setDays(value);
      setErrors((prev) => ({ ...prev, days: "" }));
    }
  };

  const validateForm = () => {
    const result = schema.safeParse({ days });

    if (!result.success) {
      const fieldErrors = {};
      const zodErrors = result.error?.issues || [];
      zodErrors.forEach((issue) => {
        const key = issue.path?.[0];
        if (key && !fieldErrors[key]) {
          fieldErrors[key] = issue.message;
        }
      });
      setErrors(fieldErrors);
      return null;
    }

    setErrors({});
    return result.data;
  };

  const handleSubmit = async () => {
    const validated = validateForm();
    if (!validated) return;

    setLoading(true);

    try {
      const response = await startAnnualEmailVerification({
        days: validated.days,
        environment:
          process.env.REACT_APP_ENVIRONMENT === "production" ? "production" : "test",
      }).unwrap();

      if (response?.status == 1) {
        toast.success(Formatmessage(response?.message));
        setDays("");
      } else if (response?.status == 0) {
        toast.error(Formatmessage(response?.message));
      } else if (response?.status == 10) {
        logout();
      }
    } catch (error) {
      console.log("error", error);
      toast.error("Server error. please try again in a few minutes.");
    } finally {
      setLoading(false);
    }
  };

  const infoCards = [
    {
      label: "Start Date",
      value: formatDate(verificationSettings.start_date),
      icon: <CalendarMonthOutlinedIcon sx={{ color: "primary.main", fontSize: 20 }} />,
    },
    {
      label: "Grace Days",
      value:
        verificationSettings.grace_days === null || verificationSettings.grace_days === undefined
          ? "—"
          : `${verificationSettings.grace_days} ${verificationSettings.grace_days === 1 ? "day" : "days"}`,
      icon: <AccessTimeOutlinedIcon sx={{ color: "primary.main", fontSize: 20 }} />,
    },
    {
      label: "Verification Deadline",
      value: formatDate(verificationSettings.needed_before),
      icon: <EventAvailableOutlinedIcon sx={{ color: "primary.main", fontSize: 20 }} />,
    },
  ];

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <SoftBox
        sx={{
          pr: { xs: 1, sm: 2, md: 3 },
          pl: { xs: 1, sm: 2, md: 2 },
          top: { xs: "55%", sm: "40%" },
        }}
        position="absolute"
        width="100%"
      >
        <SoftBox width="100%" display="flex" sx={{ pb: 4 }}>
          {isLoading ? (
            <SoftBox
              sx={{
                height: "calc(70vh)",
                width: "100%",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <FadeLoader color="#3DC2EB" />
            </SoftBox>
          ) : (
            <SoftBox
              width="100%"
              display="flex"
              flexDirection="column"
              sx={{
                backgroundColor: "white.main",
                border: "1px solid #D8E0E8",
                borderRadius: "20px",
                px: { xs: 2, sm: 3, md: 4 },
                pt: { xs: 3, sm: 4 },
                pb: 0,
              }}
            >
              <SoftBox
                display="flex"
                alignItems="center"
                gap={1.5}
                sx={{
                  mb: 3,
                }}
              >
                <SoftBox
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: "10px",
                    backgroundColor: "skyblue.main",
                  }}
                >
                  <SettingsOutlinedIcon sx={{ color: "primary.main", fontSize: 22 }} />
                </SoftBox>
                <SoftTypography
                  sx={{
                    fontSize: "24px",
                    color: "#1A1C1C",
                    fontWeight: 700,
                  }}
                >
                  Settings
                </SoftTypography>
              </SoftBox>

              <SoftBox
                display="flex"
                flexDirection="column"
                gap={1.5}
                sx={{
                  mb: 3,
                  p: 2.5,
                  borderRadius: "16px",
                  border: "1px solid #D8E0E8",
                  backgroundColor: "#F9F9F9",
                }}
              >
                <SoftBox display="flex" alignItems="center" gap={1.5}>
                  <SoftBox
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: "10px",
                      backgroundColor: "white.main",
                      border: "1px solid #D8E0E8",
                    }}
                  >
                    <MarkEmailReadOutlinedIcon sx={{ color: "primary.main", fontSize: 20 }} />
                  </SoftBox>
                  <SoftBox>
                    <SoftTypography
                      sx={{
                        fontSize: 16,
                        color: "#1A1C1C",
                        fontWeight: 600,
                      }}
                    >
                      Annual Email Verification
                    </SoftTypography>
                    <SoftTypography
                      sx={{
                        fontSize: 13,
                        color: "#666666",
                        fontWeight: 400,
                      }}
                    >
                      Trigger the annual email verification process for users based on the number of
                      days provided.
                    </SoftTypography>
                  </SoftBox>
                </SoftBox>

                <SoftBox
                  display="flex"
                  gap={2}
                  sx={{
                    flexDirection: { xs: "column", sm: "row" },
                    mt: 1,
                  }}
                >
                  {infoCards.map((card) => (
                    <SoftBox
                      key={card.label}
                      display="flex"
                      alignItems="center"
                      gap={1.5}
                      sx={{
                        flex: 1,
                        p: 2,
                        borderRadius: "12px",
                        border: "1px solid #D8E0E8",
                        backgroundColor: "white.main",
                      }}
                    >
                      <SoftBox
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        sx={{
                          width: 36,
                          height: 36,
                          borderRadius: "10px",
                          backgroundColor: "skyblue.main",
                          flexShrink: 0,
                        }}
                      >
                        {card.icon}
                      </SoftBox>
                      <SoftBox>
                        <SoftTypography sx={{ fontSize: 12, color: "#666666", fontWeight: 500 }}>
                          {card.label}
                        </SoftTypography>
                        <SoftTypography sx={{ fontSize: 15, color: "#1A1C1C", fontWeight: 600 }}>
                          {card.value}
                        </SoftTypography>
                      </SoftBox>
                    </SoftBox>
                  ))}
                </SoftBox>

                <SoftBox
                  display="flex"
                  flexDirection="column"
                  gap={1}
                  sx={{ width: { xs: "100%", sm: "50%" }, mt: 1 }}
                >
                  <SoftTypography sx={labelSx}>
                    Days <span style={{ color: "#ea0606" }}>*</span>
                  </SoftTypography>
                  <SoftInput
                    name="days"
                    value={days}
                    onChange={handleChange}
                    type="text"
                    inputMode="numeric"
                    placeholder="Enter number of days"
                    error={Boolean(errors.days)}
                    sx={inputFieldSx}
                  />
                  <span style={{ fontSize: 12, color: "#666666" }}>
                   Note: You can enter a number from 1 to 365.
                  </span>
                  {errors.days && (
                    <SoftTypography sx={{ fontSize: 12, color: "error.main" }}>
                      {errors.days}
                    </SoftTypography>
                  )}
                </SoftBox>
              </SoftBox>

              <SoftBox
                display="flex"
                justifyContent="center"
                sx={{
                  mx: { xs: -2, sm: -3, md: -4 },
                  px: { xs: 2, sm: 3, md: 4 },
                }}
              >
                <SoftBox sx={{ width: "90%", borderTop: "1px solid #D8E0E8" }} />
              </SoftBox>

              <SoftBox
                sx={{
                  px: 0,
                  py: 2.5,
                }}
              >
                <SoftBox
                  display="flex"
                  alignItems="center"
                  justifyContent="flex-end"
                  gap={2}
                  sx={{ flexDirection: { xs: "column", sm: "row" } }}
                >
                  <SoftButton
                    onClick={handleSubmit}
                    disabled={loading}
                    color="primary"
                    variant="contained"
                    sx={{
                      textTransform: "none",
                      fontSize: 15,
                      fontWeight: 400,
                      width: { xs: "100%", sm: "260px" },
                    }}
                  >
                    {loading ? "Loading..." : "Start Email Verification"}
                  </SoftButton>
                </SoftBox>
              </SoftBox>
            </SoftBox>
          )}
        </SoftBox>
      </SoftBox>
    </DashboardLayout>
  );
}

export default Settings;
